import type { ToolPart } from '#models/ChatMessageModelSpec.js';

import { z } from 'zod';

import { BaseWidgetSchema, WidgetTypeSchema } from '#models/schemas/widget-schemas.js';

/**
 * Canonical runtime-agnostic schema module shared by the chat runtimes.
 */

export interface ActionResult<T = unknown> {
  result: T;
}

export interface ActionRunOptions<Chunk = unknown, Context = any> {
  abortSignal?: AbortSignal;
  context?: Context;
  sendChunk?: (chunk: Chunk) => void | Promise<void>;
}

export interface ActionMetadata {
  actionType?: string;
  name: string;
  description?: string;
  inputSchema?: any;
  outputSchema?: any;
  streamSchema?: any;
  metadata?: Record<string, unknown>;
}

export interface ToolAction<Input = unknown, Output = unknown, Context = unknown> {
  (input?: Input, options?: any): Promise<any>;
  __action: ActionMetadata;
  name?: string;
  run(input: Input, options?: any): Promise<any>;
}

export type ToolArgument = ToolAction;

export type GenerateOptions<Config = any> = any;

export interface Chat {
  messages?: unknown[];
  send?: (input: unknown, options?: unknown) => Promise<unknown>;
  sendStream?: (input: unknown, options?: unknown) => { stream: AsyncIterable<unknown>; response: Promise<unknown> };
}

export interface GenkitBeta {
  registry?: unknown;
  [key: string]: unknown;
}

export type JSONSchema = Record<string, unknown>;

export const ToolRequestSchema = z.object({
  /** The call id or reference for a specific request. */
  ref: z.string().optional(),
  /** The name of the tool to call. */
  name: z.string(),
  /** The input parameters for the tool, usually a JSON object. */
  input: z.unknown().optional(),
});
export const ToolResponseSchema = z.object({
  /** The call id or reference for a specific request. */
  ref: z.string().optional(),
  /** The name of the tool. */
  name: z.string(),
  /** The output data returned from the tool, usually a JSON object. */
  output: z.unknown().optional(),
});
export const MediaSchema = z.object({
  /** The media content type. Inferred from data uri if not provided. */
  contentType: z.string().optional(),
  /** A `data:` or `https:` uri containing the media content.  */
  url: z.string(),
});
export const ResourceSchema = z.object({ uri: z.string() });

export interface ToolResponse {
  ref?: string;
  name: string;
  output?: unknown;
}
export interface ToolRequest {
  ref?: string;
  name: string;
  input?: unknown;
}
export interface Media {
  contentType?: string;
  url: string;
}
export interface Resource {
  uri: string;
}

/**
 * Zod schema of message part.
 */
// export const MessagePartSchema = gk.PartSchema.or(WidgetPartSchema);
export const BaseMessagePartSchema = z
  .object({
    metadata: z.record(z.string(), z.unknown()).optional(),
    /** Marks this part as temporary (for UI-only display, not persisted to Firestore) */
    temporary: z.boolean().optional(),
  })
  .strip();

export const CustomPartSchema = BaseMessagePartSchema.extend({ custom: z.record(z.string(), z.unknown()) });
export const DataPartSchema = BaseMessagePartSchema.extend({ data: z.unknown().optional() });
export const MediaPartSchema = BaseMessagePartSchema.extend({ media: MediaSchema });
/**
 * Reasoning detail entry for Mastra compatibility.
 * Mastra's AIV5Adapter.toUIMessage checks `part.details?.length` at line 178 to decide
 * whether to include reasoning parts. Without details, signature-only carriers get dropped.
 */
export const ReasoningDetailSchema = z.object({
  type: z.enum(['text', 'redacted']),
  text: z.string().optional(),
  data: z.string().optional(),
  signature: z.string().optional(),
});
export type ReasoningDetail = z.infer<typeof ReasoningDetailSchema>;

/**
 * Reasoning part with Mastra-compatible fields for round-trip survival.
 * - `reasoning`: The reasoning text content
 * - `details`: Array of detail entries (required for Mastra conversion survival)
 * - `signature`: Top-level thought signature for Gemini replay compatibility
 */
export const ReasoningPartSchema = BaseMessagePartSchema.extend({
  reasoning: z.string(),
  details: z.array(ReasoningDetailSchema).optional(),
  signature: z.string().optional(),
});
export const ResourcePartSchema = BaseMessagePartSchema.extend({ resource: ResourceSchema });
export const TextPartSchema = BaseMessagePartSchema.extend({ text: z.string() });
export const ToolRequestPartSchema = BaseMessagePartSchema.extend({ toolRequest: ToolRequestSchema });
export const ToolResponsePartSchema = BaseMessagePartSchema.extend({ toolResponse: ToolResponseSchema });
export const WidgetPartSchema = BaseMessagePartSchema.extend({
  widget: z.object({ type: WidgetTypeSchema, payload: BaseWidgetSchema.passthrough() }),
});

export interface BaseMessagePart {
  metadata?: Record<string, unknown>;
  /** Marks this part as temporary (for UI-only display, not persisted to Firestore) */
  temporary?: boolean;
}

export const MessagePartSchema = z.union([
  TextPartSchema, // Most common, check first
  MediaPartSchema,
  ReasoningPartSchema,
  ResourcePartSchema,
  ToolRequestPartSchema,
  ToolResponsePartSchema,
  WidgetPartSchema,
  CustomPartSchema,
  DataPartSchema, // Catch-all LAST - matches anything due to optional data field
]);
export type MessagePartSchema = typeof MessagePartSchema;

export type MessagePartIn = z.input<MessagePartSchema>;
export type CustomPart = z.infer<typeof CustomPartSchema>;
export type DataPart = z.infer<typeof DataPartSchema>;
export type MediaPart = z.infer<typeof MediaPartSchema>;
export type ReasoningPart = z.infer<typeof ReasoningPartSchema>;
export type ResourcePart = z.infer<typeof ResourcePartSchema>;
export type TextPart = z.infer<typeof TextPartSchema>;
export type ToolRequestPart = z.infer<typeof ToolRequestPartSchema>;
export type ToolResponsePart = z.infer<typeof ToolResponsePartSchema>;
export type WidgetPart = z.infer<typeof WidgetPartSchema>;

export type MessagePart =
  | CustomPart
  | DataPart
  | MediaPart
  | ReasoningPart
  | ResourcePart
  | TextPart
  | ToolRequestPart
  | ToolResponsePart
  | WidgetPart;

export const ClientMessagePartSchema = TextPartSchema.or(MediaPartSchema).or(DataPartSchema);

export type ClientMessagePart = z.infer<typeof ClientMessagePartSchema>;

// We need both metadata and embedMetadata because they can
// contain the same fields (e.g. video start/stop) with different values.
export const DocumentDataSchema = z.object({
  content: z.array(ClientMessagePartSchema),
  metadata: z.record(z.string(), z.any()).optional(),
});

export const MessageRoleSchema = z.enum(['system', 'user', 'model', 'tool']);

export type MessageRole = z.infer<typeof MessageRoleSchema>;

export const MessageDataSchema = z.object({
  role: MessageRoleSchema,
  content: z.array(MessagePartSchema),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export interface MessageData {
  role: MessageRole;
  content: MessagePart[];
  metadata?: Record<string, unknown>;
}

export const ModelInfoSchema = z.object({
  /** Acceptable names for this model (e.g. different versions). */
  versions: z.array(z.string()).optional(),
  /** Friendly label for this model (e.g. "Google AI - Gemini Pro") */
  label: z.string().optional(),
  /** Model Specific configuration. */
  configSchema: z.record(z.string(), z.any()).optional(),
  /** Supported model capabilities. */
  supports: z
    .object({
      /** Model can process historical messages passed with a prompt. */
      multiturn: z.boolean().optional(),
      /** Model can process media as part of the prompt (multimodal input). */
      media: z.boolean().optional(),
      /** Model can perform tool calls. */
      tools: z.boolean().optional(),
      /** Model can accept messages with role "system". */
      systemRole: z.boolean().optional(),
      /** Model can output this type of data. */
      output: z.array(z.string()).optional(),
      /** Model supports output in these content types. */
      contentType: z.array(z.string()).optional(),
      /** Model can natively support document-based context grounding. */
      context: z.boolean().optional(),
      /** Model can natively support constrained generation. */
      constrained: z.enum(['none', 'all', 'no-tools']).optional(),
      /** Model supports controlling tool choice, e.g. forced tool calling. */
      toolChoice: z.boolean().optional(),
    })
    .optional(),
  /** At which stage of development this model is.
   * - `featured` model-specs are recommended for general use.
   * - `stable` model-specs are well-tested and reliable.
   * - `unstable` model-specs are experimental and may change.
   * - `legacy` model-specs are no longer recommended for new projects.
   * - `deprecated` model-specs are deprecated by the provider and may be removed in future versions.
   */
  stage: z.enum(['featured', 'stable', 'unstable', 'legacy', 'deprecated']).optional(),
});

export const ToolDefinitionSchema = z.object({
  name: z.string(),
  description: z.string(),
  inputSchema: z
    .record(z.string(), z.any())
    .describe('Valid JSON Schema representing the input of the tool.')
    .nullish(),
  outputSchema: z
    .record(z.string(), z.any())
    .describe('Valid JSON Schema describing the output of the tool.')
    .nullish(),
  metadata: z.record(z.string(), z.any()).describe('additional metadata for this tool definition').optional(),
});

export type ToolDefinition = z.infer<typeof ToolDefinitionSchema>;

export const GenerationCommonConfigDescriptions = {
  temperature:
    'Controls the degree of randomness in token selection. A lower value is ' +
    'good for a more predictable response. A higher value leads to more ' +
    'diverse or unexpected results.',
  maxOutputTokens: 'The maximum number of tokens to include in the response.',
  topK: 'The maximum number of tokens to consider when sampling.',
  topP:
    'Decides how many possible words to consider. A higher value means ' +
    'that the model looks at more possible words, even the less likely ' +
    'ones, which makes the generated text more diverse.',
};

export const isTextPart = (part: MessagePart): part is TextPart => 'text' in part && part.text !== undefined;
export const isReasoningPart = (part: MessagePart): part is ReasoningPart =>
  'reasoning' in part && part.reasoning !== undefined;
export const isMediaPart = (part: MessagePart): part is MediaPart => 'media' in part && part.media !== undefined;
export const isToolRequestPart = (part: MessagePart): part is ToolRequestPart =>
  'toolRequest' in part && part.toolRequest !== undefined;
export const isToolResponsePart = (part: MessagePart): part is ToolResponsePart =>
  'toolResponse' in part && part.toolResponse !== undefined;
export const isToolPart = (part: MessagePart): part is ToolPart => isToolRequestPart(part) || isToolResponsePart(part);
export const isDataPart = (part: MessagePart): part is DataPart => 'data' in part && part.data !== undefined;
export const isResourcePart = (part: MessagePart): part is ResourcePart =>
  'resource' in part && part.resource !== undefined;
export const isCustomPart = (part: MessagePart): part is CustomPart => 'custom' in part && part.custom !== undefined;
export const isWidgetPart = (part: MessagePart): part is WidgetPart => 'widget' in part && part.widget !== undefined;

/**
 * Extracts and concatenates all text parts from a message's content array.
 * @param content - The content array of a message.
 * @returns The concatenated text, or undefined if no text parts are found.
 * @private
 */
export function extractText(content: MessagePart[]): string | undefined {
  const buf: string[] = [];
  if (!Array.isArray(content)) {
    console.warn('[extractText] Content is not an array:', content);
    return;
  }

  for (const part of content) {
    if (!part) continue;
    if (isTextPart(part)) buf.push(part.text);
  }

  const result = buf.length > 0 ? buf.join('') : undefined;
  if (!result && content.length > 0) {
    console.warn('[extractText] No text extracted from content:', { content });
  }
  return result;
}

const textEncoder = new TextEncoder();

/** UTF-8 byte length of a string, isomorphic across browser and Node runtimes. */
function byteLengthUtf8(value: string): number {
  return textEncoder.encode(value).length;
}

/**
 * Computes the real byte size carried by a media URL.
 *
 * For inline `data:` URIs we decode the payload to its actual byte size (base64 inflates
 * the string by ~4/3), since that is the real attachment weight. For remote URLs (e.g.
 * Firebase Storage links) only the reference string travels in the payload, so we count
 * the URL length.
 */
function mediaUrlByteLength(url: string): number {
  const commaIndex = url.indexOf(',');
  if (!url.startsWith('data:') || commaIndex === -1) {
    return byteLengthUtf8(url);
  }
  const meta = url.slice('data:'.length, commaIndex);
  const payload = url.slice(commaIndex + 1);
  if (/;base64/i.test(meta)) {
    const padding = payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((payload.length * 3) / 4) - padding);
  }
  // Percent-encoded text data URI.
  try {
    return byteLengthUtf8(decodeURIComponent(payload));
  } catch {
    return byteLengthUtf8(payload);
  }
}

/**
 * Computes the total attachment byte weight of a message's content.
 *
 * Mirrors the client-side attachment size accounting at the server boundary: only
 * attachment-bearing parts count (media parts and data parts). Plain text and model-only
 * parts (reasoning, tool calls, widgets) are excluded. Used to enforce `MAX_ATTACHMENT_BYTES`
 * server-side so direct flow callers cannot bypass the client UX cap.
 *
 * @param content - The content array of a message.
 * @returns Total attachment byte size (0 for non-array or attachment-free content).
 */
export function measureAttachmentBytes(content: MessagePart[]): number {
  if (!Array.isArray(content)) return 0;
  let total = 0;
  for (const part of content) {
    if (!part) continue;
    if (isMediaPart(part)) {
      total += mediaUrlByteLength(part.media.url);
    } else if (isDataPart(part)) {
      try {
        total += byteLengthUtf8(JSON.stringify(part.data) ?? '');
      } catch {
        // Non-serializable data parts contribute nothing measurable; ignore.
      }
    }
  }
  return total;
}

/**
 * Cleans a JSON schema by removing specific keys and standardizing types.
 * (ported from @genkit-ai/google-genai/src/common/utils.ts)
 * @param {JSONSchema} schema The JSON schema to clean.
 * @returns {JSONSchema} The cleaned JSON schema.
 */
export function cleanSchema(schema: JSONSchema): JSONSchema {
  const out = structuredClone(schema) as Record<string, unknown>;
  for (const key in out) {
    if (key === '$schema' || key === 'additionalProperties') {
      delete out[key];
      continue;
    }
    if (typeof out[key] === 'object' && out[key] !== null && !Array.isArray(out[key])) {
      out[key] = cleanSchema(out[key] as JSONSchema);
    }
    // Zod nullish() and picoschema optional fields will produce type `["string", "null"]`
    // which is not supported by the model API. Convert them to just `"string"`.
    if (key === 'type' && Array.isArray(out[key])) {
      // find the first that's not `null`.
      out[key] = out[key].find((t) => t !== 'null');
    }
  }
  return out;
}
